import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import Optional from '../../Optional/Optional';
import { useJwtHeaders } from '../../../hooks/useJwtHeaders';
import axios from 'axios';
import { saveAravotAction } from '../../../actions/saveAravotAction';
import './CreateAravosPage.scss';

interface AravotItem {
  id: number;
  kind: string;
  price: number | string;
  year?: string;
  active?: boolean;
}

const CreateAravosPage: FC = () => {
  const dispatch = useDispatch();
  const headers = useJwtHeaders();
  const admin = useSelector<RootState, boolean>(({ user }) => user.admin!);

  const [aravots, setAravots] = useState<AravotItem[]>([]);
  const [kind, setKind] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const fetchAravots = () => {
    axios
      .get('/api/v1/admin/aravots', headers)
      .then(({ data }) => {
        setAravots(data);
      })
      .catch(() => {
        axios.get('/api/v1/available-items', headers).then(({ data }) => {
          if (data && data.aravots) {
            setAravots(data.aravots);
          }
        });
      });
  };

  useEffect(() => {
    fetchAravots();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!kind.trim() || !price) return;

    setLoading(true);
    dispatch(
      saveAravotAction(kind, price, headers, () => {
        setKind('');
        setPrice('');
        setLoading(false);
        fetchAravots();
      })
    );
  };

  const handleToggleActive = (id: number, newActive: boolean) => {
    setAravots(prev => prev.map(item => (item.id === id ? { ...item, active: newActive } : item)));
    axios.patch(`/api/v1/admin/aravots/${id}`, { active: newActive }, headers).catch(() => {
      setAravots(prev => prev.map(item => (item.id === id ? { ...item, active: !newActive } : item)));
    });
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Aravos item?')) return;
    setAravots(prev => prev.filter(item => item.id !== id));
    axios.delete(`/api/v1/admin/aravots/${id}`, headers).catch(() => {
      alert('Failed to delete Aravos item');
      fetchAravots();
    });
  };

  return (
    <div className="container admin-item-page">
      <Optional renderIf={admin}>
        <h3>Admin: Manage & Create Aravos</h3>

        <div className="form-section">
          <h5 className="mb-3">Add New Aravos Item</h5>
          <form onSubmit={handleSubmit} className="inline-form">
            <div style={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Kind"
                value={kind}
                onChange={e => setKind(e.target.value)}
                required
              />
            </div>
            <div style={{ width: '120px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Price ($)"
                value={price}
                onChange={e => setPrice(e.target.value.replace(/[^0-9]/, ''))}
                required
              />
            </div>
            <div className="submit-btn-container">
              <button
                type="submit"
                className="btn btn-success"
                disabled={!kind.trim() || !price || loading}>
                {loading ? 'Saving...' : 'Save Aravos'}
              </button>
            </div>
          </form>
        </div>

        <h5>Current Aravos Items ({aravots.length})</h5>
        <table className="table table-hover table-striped">
          <thead>
            <tr>
              <th>ID</th>
              <th>Kind</th>
              <th>Price</th>
              <th>Year</th>
              <th>Active</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {aravots.map(aravot => (
              <tr key={`aravot-${aravot.id}`}>
                <td>{aravot.id}</td>
                <td>{aravot.kind}</td>
                <td>${aravot.price}</td>
                <td>{aravot.year || new Date().getFullYear()}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={aravot.active ?? true}
                    onChange={e => handleToggleActive(aravot.id, e.target.checked)}
                  />
                </td>
                <td className="text-center">
                  <i
                    className="fa delete-icon"
                    title="Delete Aravos"
                    onClick={() => handleDelete(aravot.id)}>
                    &#xf014;
                  </i>
                </td>
              </tr>
            ))}
            {aravots.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No Aravos found for current year.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Optional>
      <Optional renderIf={!admin}>
        <b>YOU MUST BE AN ADMIN TO VIEW THIS PAGE</b>
      </Optional>
    </div>
  );
};

export default CreateAravosPage;
